import { useEffect, useState } from "react";
import api from "../api/PosApi";
import { Card, Container, Row, Col, Modal } from "react-bootstrap";

function useDebounce(value: any, delay: any) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function Clientes() {
  const [term, setTerm] = useState("");
  const [clientes, setClientes] = useState<any[]>([]);
  const [departamentos, setDepartamentos] = useState<any[]>([]);
  const [municipios, setMunicipios] = useState<any[]>([]);
  const [actividades, setActividades] = useState<any[]>([]);
  const [terminoActividad, setTerminoActividad] = useState("");
  const [mensaje, setMensaje] = useState("");

  const [clienteSeleccionadoId, setClienteSeleccionadoId] = useState<string | null>(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [mostrarModalModificar, setMostrarModalModificar] = useState(false);

  const debouncedActividad = useDebounce(terminoActividad, 1000);

  const [nuevo, setNuevo] = useState({
    tipoDocumento: "36",
    numeroDocumento: "",
    nombre: "",
    direccionDepartamento: "05",
    direccionMunicipio: "06",
    direccionComplemento: "",
    telefono: "",
    correo: "",
    actividadEconomica: "",
    nrc: "",
    nombreComercial: "",
    tipoContribuyente: "Otros",
    ivaStatus: "normal"
  });

  const buscarActividad = async (termino: string) => {
    try {
      const response = await api.get(
        `/pos/actividad/filtrar?term=${encodeURIComponent(termino)}`
      );

      setActividades(response.data);
    } catch (error) {
      console.error("Error al buscar actividades activas:", error);
    }
  };

  const buscar = async () => {
    try {
      const res = await api.get(
        `/pos/clientes?term=${encodeURIComponent(term)}`
      );

      setClientes(res.data);
      setMensaje("");
    } catch {
      setMensaje("❌ Error al buscar clientes");
    }
  };

  const limpiarBusqueda = () => {
    setTerm("");
    setClientes([]);
    setMensaje("");
  };

  const limpiarFormulario = () => {
    setNuevo({
      tipoDocumento: "36",
      numeroDocumento: "",
      nombre: "",
      direccionDepartamento: "05",
      direccionMunicipio: "06",
      direccionComplemento: "",
      telefono: "",
      correo: "",
      actividadEconomica: "",
      nrc: "",
      nombreComercial: "",
      tipoContribuyente: "Otros",
      ivaStatus: "normal"
    });

    setTerminoActividad("");
    setActividades([]);
    setClienteSeleccionadoId(null);
    setModoEdicion(false);
    setMostrarModalModificar(false);
  };

  const crearCliente = async () => {
    try {
      if (
        !nuevo.numeroDocumento ||
        !nuevo.nombre ||
        !nuevo.telefono ||
        !nuevo.correo ||
        !nuevo.nombreComercial
      ) {
        setMensaje(
          "⚠️ Debes llenar todos los campos obligatorios (Documento, nombre, teléfono, correo, nombre comercial)."
        );
        return;
      }

      if (nuevo.nrc !== "" && !nuevo.actividadEconomica) {
        setMensaje(
          "⚠️ No has seleccionado la actividad económica para este contribuyente."
        );
        return;
      }

      const res = await api.post("/pos/clientes", nuevo);

      setMensaje(`✅ Cliente creado: ${res.data.nombre}`);

      limpiarFormulario();

      if (term.trim() !== "") {
        await buscar();
      }
    } catch {
      setMensaje("❌ Error al crear cliente");
    }
  };

  const seleccionarCliente = (cliente: any) => {
    setClienteSeleccionadoId(cliente.id);
    setModoEdicion(true);

    setNuevo({
      tipoDocumento: cliente.tipoDocumento ?? "36",
      numeroDocumento: cliente.numeroDocumento ?? "",
      nombre: cliente.nombre ?? "",
      direccionDepartamento: cliente.direccionDepartamento ?? "05",
      direccionMunicipio: cliente.direccionMunicipio ?? "06",
      direccionComplemento: cliente.direccionComplemento ?? "",
      telefono: cliente.telefono ?? "",
      correo: cliente.correo ?? "",
      actividadEconomica: cliente.actividadEconomica ?? "",
      nrc: cliente.nrc ?? "",
      nombreComercial: cliente.nombreComercial ?? "",
      tipoContribuyente: cliente.tipoContribuyente ?? "Otros",
      ivaStatus: cliente.ivaStatus ?? "normal"
    });

    setTerminoActividad(
      cliente.actividadEconomicaNombre ??
      cliente.actividadEconomica ??
      ""
    );

    setActividades([]);
    setMensaje(`Cliente seleccionado para modificar: ${cliente.nombre}`);
  };

  const abrirModalModificar = () => {
    if (!clienteSeleccionadoId) {
      setMensaje("⚠️ Debes seleccionar un cliente antes de modificar.");
      return;
    }

    if (
      !nuevo.numeroDocumento ||
      !nuevo.nombre ||
      !nuevo.telefono ||
      !nuevo.correo ||
      !nuevo.nombreComercial
    ) {
      setMensaje("⚠️ Debes llenar todos los campos obligatorios antes de modificar.");
      return;
    }

    if (nuevo.nrc !== "" && !nuevo.actividadEconomica) {
      setMensaje(
        "⚠️ No has seleccionado la actividad económica para este contribuyente."
      );
      return;
    }

    setMostrarModalModificar(true);
  };

  const modificarCliente = async () => {
    try {
      if (!clienteSeleccionadoId) {
        setMensaje("⚠️ Debes seleccionar un cliente para modificar.");
        return;
      }

      const res = await api.put(
        `/pos/clientes/${clienteSeleccionadoId}`,
        nuevo
      );

      setMensaje(`✅ Cliente modificado: ${res.data.nombre ?? nuevo.nombre}`);

      setMostrarModalModificar(false);

      if (term.trim() !== "") {
        await buscar();
      }

      limpiarFormulario();
    } catch (error) {
      console.error(error);
      setMensaje("❌ Error al modificar cliente");
      setMostrarModalModificar(false);
    }
  };

  const handleChange = (e: any) => {
    const { name, value, type } = e.target;

    const limpio =
      type === "text" || type === "tel"
        ? value.replace(/[\s-]/g, "")
        : value;

    setNuevo((prev) => ({ ...prev, [name]: limpio }));
  };

  const hanndleClickActividad = (actividad: any) => {
    setNuevo({ ...nuevo, actividadEconomica: actividad.codigo });
    setActividades([]);
    setTerminoActividad(actividad.nombre);
  };

  const municipiosFiltrados = municipios.filter(
    (m: any) => m.departamentoCodigo === nuevo.direccionDepartamento
  );

  useEffect(() => {
    const fetchCatalogos = async () => {
      try {
        const [depsRes, munRes] = await Promise.all([
          api.get("/pos/departamentos/todos"),
          api.get("/pos/municipios/todos")
        ]);

        setDepartamentos(depsRes.data);
        setMunicipios(munRes.data);
      } catch (err) {
        console.log("Error al cargar municipio o departamento: " + err);
      }
    };

    fetchCatalogos();
  }, []);

  useEffect(() => {
    if (debouncedActividad) {
      buscarActividad(debouncedActividad);
    } else {
      setActividades([]);
    }
  }, [debouncedActividad]);

  return (
    <Container>
      <Row>
        <Col>
          <Card className="card-black mb-3">
            <Card.Header>
              <h3>Gestión de Clientes</h3>
            </Card.Header>

            <Card.Body>
              <Row>
                <Col xl={10}>
                  <div className="input-group">
                    <input
                      placeholder="Nombre o NIT"
                      className="form-control"
                      value={term}
                      onChange={(e) => setTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          buscar();
                        }
                      }}
                    />

                    {term && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={limpiarBusqueda}
                        title="Limpiar búsqueda"
                      >
                        X
                      </button>
                    )}
                  </div>
                </Col>

                <Col xl={2}>
                  <button
                    onClick={buscar}
                    className="btn btn-primary w-100"
                  >
                    Buscar
                  </button>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col>
                  {clientes.length > 0 && (
                    <ul className="list-group">
                      {clientes.map((c: any) => (
                        <li
                          key={c.id}
                          className="list-group-item list-group-item-action"
                          style={{ cursor: "pointer" }}
                          onClick={() => seleccionarCliente(c)}
                        >
                          <strong>{c.nombre}</strong> ({c.numeroDocumento})
                        </li>
                      ))}
                    </ul>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="card-black">
            <Card.Header>
              <h3>{modoEdicion ? "Modificar Cliente" : "Añadir nuevos Clientes"}</h3>
            </Card.Header>

            <Card.Body>
              <Row className="mb-3">
                <Col className="form-group">
                  <label htmlFor="dui">Documento DUI|NIT:</label>
                  <input
                    className="form-control"
                    id="dui"
                    name="numeroDocumento"
                    placeholder="Documento (DUI/NIT)"
                    value={nuevo.numeroDocumento}
                    onChange={(e) =>
                      setNuevo({
                        ...nuevo,
                        numeroDocumento: e.target.value.replace(/[^0-9]/g, "")
                      })
                    }
                  />
                </Col>

                <Col className="form-group">
                  <label htmlFor="nombre">Nombre:</label>
                  <input
                    id="nombre"
                    name="nombre"
                    placeholder="Nombre"
                    className="form-control"
                    value={nuevo.nombre}
                    onChange={(e) =>
                      setNuevo({ ...nuevo, nombre: e.target.value })
                    }
                  />
                </Col>

                <Col className="form-group">
                  <label htmlFor="phone">Teléfono:</label>
                  <input
                    id="phone"
                    name="telefono"
                    className="form-control"
                    placeholder="Teléfono"
                    value={nuevo.telefono}
                    onChange={(e) =>
                      setNuevo({
                        ...nuevo,
                        telefono: e.target.value.replace(/[^0-9]/g, "")
                      })
                    }
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col className="form-group">
                  <label htmlFor="comercialName">Nombre Comercial:</label>
                  <input
                    placeholder="Nombre Comercial"
                    id="comercialName"
                    name="nombreComercial"
                    className="form-control"
                    value={nuevo.nombreComercial}
                    onChange={(e) =>
                      setNuevo({
                        ...nuevo,
                        nombreComercial: e.target.value
                      })
                    }
                  />
                </Col>

                <Col className="form-group">
                  <label htmlFor="direccion">Dirección:</label>
                  <input
                    placeholder="Dirección"
                    id="direccion"
                    name="direccionComplemento"
                    className="form-control"
                    value={nuevo.direccionComplemento}
                    onChange={(e) =>
                      setNuevo({
                        ...nuevo,
                        direccionComplemento: e.target.value
                      })
                    }
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col className="form-group">
                  <label htmlFor="mail">Correo:</label>
                  <input
                    id="mail"
                    name="correo"
                    className="form-control"
                    placeholder="Correo"
                    type="email"
                    value={nuevo.correo}
                    onChange={(e) =>
                      setNuevo({ ...nuevo, correo: e.target.value })
                    }
                  />
                </Col>

                <Col className="form-group">
                  <label htmlFor="nrc">NRC:</label>
                  <input
                    id="nrc"
                    name="nrc"
                    className="form-control"
                    placeholder="NRC"
                    value={nuevo.nrc}
                    onChange={(e) =>
                      setNuevo({
                        ...nuevo,
                        nrc: e.target.value.replace(/[^0-9]/g, "")
                      })
                    }
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col className="form-group">
                  <label htmlFor="actEc">Actividad Económica:</label>
                  <input
                    id="actEc"
                    name="actividadEconomica"
                    className="form-control"
                    placeholder="Actividad Económica"
                    value={terminoActividad}
                    onChange={(e) => setTerminoActividad(e.target.value)}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xl={12}>
                  {actividades.length > 0 && (
                    <ul className="cliente-suggestions list-group">
                      {actividades.map((p: any) => (
                        <li
                          className="list-group-item list-group-item-action"
                          key={p.codigo}
                          style={{ cursor: "pointer" }}
                          onClick={() => hanndleClickActividad(p)}
                        >
                          {p.codigo} - {p.nombre}
                        </li>
                      ))}
                    </ul>
                  )}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col className="form-group">
                  <label htmlFor="dpto">Departamento:</label>
                  <select
                    id="dpto"
                    className="form-control"
                    name="direccionDepartamento"
                    value={nuevo.direccionDepartamento}
                    onChange={handleChange}
                  >
                    {departamentos.map((d: any) => (
                      <option key={d.codigo} value={d.codigo}>
                        {d.nombre}
                      </option>
                    ))}
                  </select>
                </Col>

                <Col className="form-group">
                  <label htmlFor="municipio">Municipio:</label>
                  <select
                    id="municipio"
                    className="form-control"
                    name="direccionMunicipio"
                    value={nuevo.direccionMunicipio}
                    onChange={handleChange}
                  >
                    {municipiosFiltrados.map((m: any) => (
                      <option key={m.codigo} value={m.codigo}>
                        {m.nombre}
                      </option>
                    ))}
                  </select>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col className="form-group">
                  <label htmlFor="tipoContribuyente">Tipo Contribuyente:</label>
                  <select
                    name="tipoContribuyente"
                    id="tipoContribuyente"
                    className="form-control"
                    value={nuevo.tipoContribuyente}
                    onChange={(e) =>
                      setNuevo({
                        ...nuevo,
                        tipoContribuyente: e.target.value
                      })
                    }
                  >
                    <option value="Otros">Otros</option>
                    <option value="Grande">Grande</option>
                    <option value="Mediano">Mediano</option>
                  </select>
                </Col>

                <Col className="form-group">
                  <label htmlFor="ivaStatus">Tipo IVA:</label>
                  <select
                    name="ivaStatus"
                    id="ivaStatus"
                    className="form-control"
                    value={nuevo.ivaStatus}
                    onChange={(e) =>
                      setNuevo({ ...nuevo, ivaStatus: e.target.value })
                    }
                  >
                    <option value="normal">Normal</option>
                    <option value="retiene">Retiene</option>
                    <option value="exento">Exento</option>
                  </select>
                </Col>
              </Row>

              <Row>
                <Col>
                  <button
                    className="btn btn-success w-100"
                    onClick={crearCliente}
                    disabled={modoEdicion}
                  >
                    Guardar Cliente
                  </button>
                </Col>

                <Col>
                  <button
                    className="btn btn-warning w-100"
                    onClick={abrirModalModificar}
                    disabled={!modoEdicion}
                  >
                    Modificar Cliente
                  </button>
                </Col>

                <Col>
                  <button
                    className="btn btn-secondary w-100"
                    onClick={limpiarFormulario}
                    disabled={!modoEdicion}
                  >
                    Cancelar
                  </button>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col>
                  {mensaje && <p>{mensaje}</p>}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={mostrarModalModificar}
        onHide={() => setMostrarModalModificar(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar modificación</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>¿Deseas modificar este cliente?</p>

          <p>
            <strong>{nuevo.nombre}</strong>
          </p>

          <p>
            Documento: <strong>{nuevo.numeroDocumento}</strong>
          </p>
        </Modal.Body>

        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setMostrarModalModificar(false)}
          >
            No
          </button>

          <button
            className="btn btn-primary"
            onClick={modificarCliente}
          >
            Sí, modificar
          </button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}